import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'

export type TrendBasedFibonacciDrawingState = {
    p1?: Point,
    p2?: Point,
    p3?: Point
}

export type TrendBasedFibonacciDrawingSettings = {
    showTrendLine: boolean,
    extendLeft: boolean,
    extendRight: boolean,
    lineWidth: number,
    labelColor: ColorValue,
}

class TrendBasedFibonacciDrawingTool extends DrawingBase<any, TrendBasedFibonacciDrawingSettings, TrendBasedFibonacciDrawingState> {

    area: number = 0
    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0,
        p3x: 0,
        p3y: 0
    }
    sectionLevels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.618, 2.618, 3.618, 4.236]
    sections: number[] = []
    sectionColors: string[] = ['#787b86', '#f23645', '#ff9800', '#4caf50', '#089981', '#00bcd4', '#787b86', '#2962ff', '#f23645', '#9c27b0', '#e91e63']

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        this.calculateAttributes()
        const { p3 } = this._state
        let { p1x, p2x, p1y, p2y, p3x, p3y } = this.scaledCoordinates
        if (!p1x || !p2x || !p1y || !p2y) return

        let previousLevel = 0
        const startX = this._settings.extendLeft ? 0 : (p2x < p3x ? p2x : p3x)
        const endX = this._settings.extendRight ? this.canvasWidth : (p2x > p3x ? p2x : p3x)
        ctx.beginPath()
        ctx.moveTo(p1x, p1y)
        ctx.lineTo(p2x, p2y)
        if (this._settings.showTrendLine) {

            if (p3 && p3x && p3y) {
                ctx.lineTo(p3x, p3y)
            }
            ctx.globalAlpha = 0.5

        }
        ctx.strokeStyle = this.sectionColors[0]
        ctx.lineWidth = window.devicePixelRatio
        ctx.setLineDash([8, 4])
        ctx.stroke()
        ctx.closePath()
        ctx.setLineDash([])
        this.sections.map((s, i) => {
            const scaledY = scaledCoordinate(this.priceToCoordinate(s) || 0, this.verticalPixelRatio)
            if (previousLevel) {
                ctx.globalAlpha = 0.15
                ctx.fillStyle = this.sectionColors[i]
                ctx.beginPath()
                ctx.fillRect(startX, previousLevel < scaledY ? previousLevel : scaledY, Math.abs(endX - startX), Math.abs(scaledY - previousLevel))
                ctx.closePath()
            }

            previousLevel = scaledY

        })

        this.sections.map((s, i) => {
            ctx.globalAlpha = 0.7
            ctx.strokeStyle = this.sectionColors[i]
            ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio
            const scaledY = scaledCoordinate(this.priceToCoordinate(s) || 0, this.verticalPixelRatio)
            ctx.beginPath()
            ctx.moveTo(startX, scaledY)
            ctx.lineTo(endX, scaledY)
            ctx.stroke()
            ctx.closePath()
            this.renderSectionText(ctx, i)
        })
    };

    renderSectionText(ctx: CanvasRenderingContext2D, index: number) {
        let { p2x, p3x } = this.scaledCoordinates
        if (!p2x || !p3x) return
        const level = this.sectionLevels[index]
        const price = this.sections[index]
        const scaledY = scaledCoordinate(this.priceToCoordinate(price) || 0, this.verticalPixelRatio)
        const text = `${level} (${price.toFixed(2)})`
        ctx.font = 12 * window.devicePixelRatio + 'px sans-serif'
        ctx.fillStyle = this.sectionColors[index]
        ctx.textBaseline = 'bottom'
        const dim = ctx.measureText(text)
        const textWidth = dim.width
        let startX = this._settings.extendLeft ? 5 : Math.min(p2x, p3x) - textWidth - 5
        ctx.fillText(text, startX, scaledY - 2)
    }

    calculateSections() {
        const { p1, p2, p3 } = this._state
        if (!p1 || !p2 || !p3) return

        const range = p2.y - p1.y

        this.sections = this.sectionLevels.map((v) => p3.y + range * v)

    }

    calculateAttributes() {
        const { p1, p2, p3 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
        }
        if (!p3) return
        let p3x = this.timeToCoordinate(p3.x)
        let p3y = this.priceToCoordinate(p3.y)
        if (p3x && p3y) {
            this.scaledCoordinates.p3x = scaledCoordinate(p3x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p3y = scaledCoordinate(p3y, this.verticalPixelRatio) as Coordinate
        }
        this.calculateSections()
    }


    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
        } else if (this.lastAddedPoint.id == 'p2') {
            point.id = 'p3'
            this._state.p3 = point
            !preview && this.onDrawingEnd()
        }
        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2, p3 } = this._state

        p1 && handles.push(p1)
        p2 && handles.push(p2)
        p3 && handles.push(p3)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p2x, p3x } = this.scaledCoordinates

        if (!this.sections.length) return null

        const start = scaledCoordinate(this.priceToCoordinate(this.sections[0]) || 0, this.verticalPixelRatio)
        const end = scaledCoordinate(this.priceToCoordinate(this.sections[this.sections.length - 1]) || 0, this.verticalPixelRatio)

        const startX = this._settings.extendLeft ? 0 : (p3x < p2x ? p3x : p2x)
        const endX = this._settings.extendRight ? this.canvasWidth : (p3x > p2x ? p3x : p2x)

        const minY = Math.min(start, end), maxY = Math.max(start, end)

        if (scaledX < startX || scaledX > endX || scaledY < minY || scaledY > maxY) return null
        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
                case 'p3':
                    this._state.p3 = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        this._state[id as keyof typeof this._state]!.x = time
        this._state[id as keyof typeof this._state]!.y = price
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {

        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => a.y - b.y)
        return [{
            id: 'price',
            p1: sorted[0].y,
            p2: sorted[1].y,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => (a.x as number) - (b.x as number))
        return [{
            id: 'timer',
            p1: sorted[0].x,
            p2: sorted[1].x,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<TrendBasedFibonacciDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#6397d4',
                        opacity: 0.7
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                },
                {
                    name: 'showTrendLine',
                    label: 'Show Trend Line',
                    type: SettingType.BOOLEAN,
                    defaultValue: true,
                    custom: true,
                    visible: true
                },
                {
                    name: 'extendLeft',
                    label: 'Extend Left',
                    type: SettingType.BOOLEAN,
                    defaultValue: false,
                    custom: true,
                    visible: true
                },
                {
                    name: 'extendRight',
                    label: 'Extend Right',
                    type: SettingType.BOOLEAN,
                    defaultValue: false,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): TrendBasedFibonacciDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof TrendBasedFibonacciDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = ['labelColor', 'lineWidth']
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'trendBasedFibonacci'
    }
}

export default TrendBasedFibonacciDrawingTool